import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CashFlow, CashFlowCurrency, TrialBalance as TrialBalanceDto, TrialBalanceCurrency, TrialBalanceRow } from "../types";
import { financeReportService } from "../services/dataService";
import { formatCurrency } from "../utils/formatters";
import { usePermissions } from "../context/PermissionContext";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import "./TrialBalance.css";

/**
 * Mizan ve Nakit Akış — finans modülü Faz 4.
 * Yönetim mizanı alt defterlerden türetilir (kasa/banka, çek, alıcı, satıcı, kasa kaynaklı gelir-gider);
 * Tekdüzen kodları mali müşavir eşlemesi içindir. Nakit akış: açık kalemler + çek vadeleri haftalık kovalara.
 * Sözlük `tb-*`, Finans ekranlarıyla aynı lacivert dil.
 */

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const money = (v: number, cur: string) => formatCurrency(v, cur || "TRY");
const errorText = (error: any, fallback: string) => { const d = error?.response?.data; if (typeof d === "string") return d; return d?.detail || d?.title || d?.message || fallback; };
const GROUP: Record<string, string> = { hazir: "Hazır değer", alici: "Alıcı", satici: "Borç", gelir: "Gelir", gider: "Gider" };
type Preset = "month" | "prev" | "quarter" | "year" | "custom";
const presetRange = (p: Preset): { from: string; to: string } => {
  const d = new Date();
  if (p === "prev") return { from: iso(new Date(d.getFullYear(), d.getMonth() - 1, 1)), to: iso(new Date(d.getFullYear(), d.getMonth(), 0)) };
  if (p === "quarter") { const q = Math.floor(d.getMonth() / 3) * 3; return { from: iso(new Date(d.getFullYear(), q, 1)), to: iso(new Date(d.getFullYear(), q + 3, 0)) }; }
  if (p === "year") return { from: iso(new Date(d.getFullYear(), 0, 1)), to: iso(new Date(d.getFullYear(), 11, 31)) };
  return { from: iso(new Date(d.getFullYear(), d.getMonth(), 1)), to: iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)) };
};

const I = {
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" /></svg>,
  excel: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2h9l5 5v15H6z" /><path d="M14 2v6h6M9 12l6 6M15 12l-6 6" /></svg>,
  scale: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18M4 7h16" /><path d="M6 7l-3 6a3 3 0 0 0 6 0zM18 7l-3 6a3 3 0 0 0 6 0z" /></svg>,
  flow: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l5-5 4 4 5-6 4 4" /><path d="M3 21h18" /></svg>,
  arrow: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>,
};

const TrialBalance: React.FC = () => {
  const { has } = usePermissions();
  const navigate = useNavigate();
  const canExport = has("trial-balance:export");
  const [tab, setTab] = useState<"mizan" | "nakit">("mizan");
  const [preset, setPreset] = useState<Preset>("month");
  const [range, setRange] = useState(presetRange("month"));
  const [tb, setTb] = useState<TrialBalanceDto | null>(null);
  const [cf, setCf] = useState<CashFlow | null>(null);
  const [weeks, setWeeks] = useState(8);
  const [initial, setInitial] = useState(true);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [alert, setAlert] = useState<{ isOpen: boolean; message: string; type: "info" | "success" | "warning" | "error" }>({ isOpen: false, message: "", type: "info" });
  const fail = (message: string) => setAlert({ isOpen: true, message, type: "error" });

  const loadTb = useCallback(async () => {
    setBusy(true);
    try { setTb(await financeReportService.trialBalance(range.from, range.to)); }
    catch (err) { fail(errorText(err, "Mizan yüklenemedi.")); }
    finally { setBusy(false); setInitial(false); }
  }, [range]);
  const loadCf = useCallback(async () => {
    setBusy(true);
    try { setCf(await financeReportService.cashFlow(weeks)); }
    catch (err) { fail(errorText(err, "Nakit akış yüklenemedi.")); }
    finally { setBusy(false); setInitial(false); }
  }, [weeks]);
  useEffect(() => { loadTb(); }, [loadTb]);
  useEffect(() => { if (tab === "nakit") loadCf(); }, [tab, loadCf]);

  const applyPreset = (p: Preset) => { setPreset(p); if (p !== "custom") setRange(presetRange(p)); };
  const download = async () => {
    setBusy(true);
    try {
      const blob = await financeReportService.accountantPack(range.from, range.to);
      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `nedaris-mizan-${range.from}-${range.to}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch (err) { fail(errorText(err, "Excel hazırlanamadı.")); }
    finally { setBusy(false); }
  };
  const toggle = (k: string) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  if (initial) return <PageLoader message="Mizan hazırlanıyor..." />;
  const tryC = tb?.currencies.find((c) => c.currency === "TRY");

  const Row: React.FC<{ r: TrialBalanceRow; cur: string; k: string }> = ({ r, cur, k }) => {
    const has = r.children.length > 0;
    const on = !!open[k];
    return (<>
      <tr className="grp static">
        <td className="tb-code">{has && <button className="tb-toggle" onClick={() => toggle(k)}>{on ? "▾" : "▸"}</button>}{r.code}</td>
        <td>{r.link ? <button className="tb-link" onClick={() => navigate(r.link!)}>{r.name}</button> : r.name}<span className={`tb-grp ${r.group}`}>{GROUP[r.group]}</span></td>
        <td className={`num ${r.opening < 0 ? "tb-neg" : r.opening === 0 ? "tb-muted" : ""}`}>{money(r.opening, cur)}</td>
        <td className="num">{r.periodDebit ? money(r.periodDebit, cur) : <span className="tb-muted">—</span>}</td>
        <td className="num">{r.periodCredit ? money(r.periodCredit, cur) : <span className="tb-muted">—</span>}</td>
        <td className="num">{r.debitBalance ? money(r.debitBalance, cur) : <span className="tb-muted">—</span>}</td>
        <td className="num">{r.creditBalance ? money(r.creditBalance, cur) : <span className="tb-muted">—</span>}</td>
      </tr>
      {has && on && r.children.map((c, i) => (
        <tr key={i} className="child static">
          <td>{c.code}</td><td>{c.name}</td>
          <td className={`num ${c.opening < 0 ? "tb-neg" : ""}`}>{c.opening ? money(c.opening, cur) : "—"}</td>
          <td className="num">{c.periodDebit ? money(c.periodDebit, cur) : "—"}</td>
          <td className="num">{c.periodCredit ? money(c.periodCredit, cur) : "—"}</td>
          <td className="num">{c.debitBalance ? money(c.debitBalance, cur) : "—"}</td>
          <td className="num">{c.creditBalance ? money(c.creditBalance, cur) : "—"}</td>
        </tr>
      ))}
    </>);
  };

  const Flow: React.FC<{ c: CashFlowCurrency }> = ({ c }) => {
    const max = Math.max(1, ...c.realized.map((b) => Math.max(b.in, b.out)));
    return (
      <div className="tb-section">
        <h2>Nakit akış · {c.currency}<em>bugün {money(c.cashNow, c.currency)} · en düşük {money(c.minProjected, c.currency)}{c.minProjectedAt ? ` (${fmtDate(c.minProjectedAt)})` : ""}</em></h2>
        <div className="tb-flow">
          {c.buckets.map((b) => (
            <div key={b.key} className={`tb-bucket ${b.key === "overdue" ? "overdue" : b.key === "w0" ? "now" : ""}`} title={`${b.itemCount} kalem`}>
              <span className="l">{b.label}</span>
              <div className="io"><span className="in">+{money(b.in, c.currency)}</span><span className="out">−{money(b.out, c.currency)}</span></div>
              <span className={`proj ${b.projected < 0 ? "neg" : ""}`}>{money(b.projected, c.currency)}</span>
              <small>{b.key === "overdue" ? "vadesi geçmiş açık kalemler" : b.key === "later" ? "sonraki vadeler" : `${b.itemCount} kalem${b.chequesIn || b.chequesOut ? " · çek dahil" : ""}`}</small>
            </div>
          ))}
        </div>
        <p className="tb-note">Kovalar: alıcı açık kalemleri ve portföydeki çekler giriş, tedarikçi açık kalemleri ve verilen çekler çıkış sayılır; bakiye bugünkü nakitten kümülatif yürür. Gecikmiş kalemler ilk kovada toplanır (tahsil/ödeme zamanı belirsiz).</p>
        {c.realized.length > 0 && (<>
          <h2 style={{ marginTop: "1rem" }}>Gerçekleşen · son 8 hafta<em>haftalık ort. giriş {money(c.avgWeeklyIn, c.currency)} · çıkış {money(c.avgWeeklyOut, c.currency)}</em></h2>
          <div className="tb-bars">{c.realized.map((b) => (
            <div key={b.key} className="tb-bar" title={`${b.label}: +${money(b.in, c.currency)} / −${money(b.out, c.currency)}`}>
              <div className="cols"><i className="in" style={{ height: `${(b.in / max) * 100}%` }} /><i className="out" style={{ height: `${(b.out / max) * 100}%` }} /></div>
              <small>{b.label}</small>
            </div>
          ))}</div>
          <div className="tb-legend"><span style={{ ["--c" as any]: "var(--tb-ok)" }}>Giriş</span><span style={{ ["--c" as any]: "var(--tb-danger)" }}>Çıkış</span></div>
        </>)}
      </div>
    );
  };

  return (
    <div className="tb-page">
      <header className="tb-head">
        <div>
          <span className="tb-kicker">Finans</span>
          <h1>Mizan ve Nakit Akış</h1>
          <p>Dönem mizanı kasa, banka, çek, alıcı ve satıcı defterlerinden türetilir; Tekdüzen kodları mali müşavirin eşlemesi içindir (çift taraflı yevmiye değildir). Nakit akış, açık kalemler ve çek vadelerinden önümüzdeki haftaları tahmin eder.</p>
        </div>
        <div className="tb-head-actions">
          <button className="tb-btn" onClick={() => (tab === "mizan" ? loadTb() : loadCf())} disabled={busy}><span className={busy ? "tb-spin-icon" : ""}>{I.refresh}</span> Yenile</button>
          {canExport && <button className="tb-btn primary" onClick={download} disabled={busy}>{I.excel} Mali müşavir paketi (Excel)</button>}
        </div>
      </header>

      <div className="tb-tabs">
        <button className={tab === "mizan" ? "on" : ""} onClick={() => setTab("mizan")}>{I.scale} Mizan</button>
        <button className={tab === "nakit" ? "on" : ""} onClick={() => setTab("nakit")}>{I.flow} Nakit akış</button>
      </div>

      {tab === "mizan" && (<>
        <section className="tb-kpis">
          <div className="tb-kpi navy"><span className="l">Net varlık · TRY</span><span className="v">{money(tryC?.netWorth ?? 0, "TRY")}</span><span className="s">hazır değer + alıcı − satıcı, dönem sonu</span></div>
          <div className={`tb-kpi ${(tryC?.netResult ?? 0) < 0 ? "err" : "ok"}`}><span className="l">Dönem sonucu</span><span className="v">{money(tryC?.netResult ?? 0, "TRY")}</span><span className="s">gelir <b>{money(tryC?.income ?? 0, "TRY")}</b> − gider <b>{money(tryC?.expense ?? 0, "TRY")}</b></span></div>
          <div className="tb-kpi"><span className="l">Dönem borç / alacak</span><span className="v" style={{ fontSize: "1.05rem" }}>{money(tryC?.totalPeriodDebit ?? 0, "TRY")} / {money(tryC?.totalPeriodCredit ?? 0, "TRY")}</span><span className="s">{fmtDate(range.from)} – {fmtDate(range.to)}</span></div>
          {tb?.currencies.filter((c) => c.currency !== "TRY").map((c) => <div className="tb-kpi" key={c.currency}><span className="l">Net varlık · {c.currency}</span><span className="v">{money(c.netWorth, c.currency)}</span><span className="s">{c.rateToTry ? `kur ${c.rateToTry.toFixed(4)} · ≈ ${money(c.netWorth * c.rateToTry, "TRY")}` : "kur bulunamadı"}</span></div>)}
        </section>

        <section className={`tb-card ${busy ? "dim" : ""}`}>
          <div className="tb-toolbar">
            <div className="tb-chipgroup">
              {(["month", "prev", "quarter", "year"] as Preset[]).map((p) => <button key={p} className={preset === p ? "on" : ""} onClick={() => applyPreset(p)}>{{ month: "Bu ay", prev: "Geçen ay", quarter: "Bu çeyrek", year: "Bu yıl", custom: "" }[p]}</button>)}
            </div>
            <div className="tb-dates"><input type="date" value={range.from} onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, from: e.target.value })); }} /><span>–</span><input type="date" value={range.to} onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, to: e.target.value })); }} /></div>
            <span className="tb-hint" style={{ marginLeft: "auto" }}>{tb?.rateDate ? `Kur: ${fmtDate(tb.rateDate)} döviz satış` : ""}</span>
          </div>
          {!tb || tb.currencies.length === 0 ? <div className="tb-blank"><b>Bu dönemde hareket yok</b>Kasa, cari ya da çek hareketi girildikçe mizan burada oluşur.</div> : tb.currencies.map((c: TrialBalanceCurrency) => (
            <div key={c.currency}>
              <div className="tb-cur-head"><b>{c.currency}</b><span>dönem sonucu <b className={c.netResult < 0 ? "tb-neg" : "tb-pos"}>{money(c.netResult, c.currency)}</b> · net varlık <b>{money(c.netWorth, c.currency)}</b></span></div>
              <div className="tb-table-wrap"><table className="tb-table">
                <thead><tr><th style={{ width: "5rem" }}>Kod</th><th>Hesap</th><th className="num">Dönem başı</th><th className="num">Borç</th><th className="num">Alacak</th><th className="num">Borç bakiye</th><th className="num">Alacak bakiye</th></tr></thead>
                <tbody>{c.rows.map((r, i) => <Row key={i} r={r} cur={c.currency} k={`${c.currency}-${r.code}-${i}`} />)}</tbody>
                <tfoot><tr><td colSpan={3}>Toplam · {c.currency}</td><td className="num">{money(c.totalPeriodDebit, c.currency)}</td><td className="num">{money(c.totalPeriodCredit, c.currency)}</td><td className="num">{money(c.totalDebitBalance, c.currency)}</td><td className="num">{money(c.totalCreditBalance, c.currency)}</td></tr></tfoot>
              </table></div>
            </div>
          ))}
          <p className="tb-note" style={{ padding: "0.75rem 1rem" }}>{tb?.note}</p>
        </section>
      </>)}

      {tab === "nakit" && (<>
        <div className="tb-section" style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <span className="tb-kicker" style={{ margin: 0 }}>Ufuk</span>
          <div className="tb-chipgroup">{[4, 8, 12, 16].map((w) => <button key={w} className={weeks === w ? "on" : ""} onClick={() => setWeeks(w)}>{w} hafta</button>)}</div>
          <span className="tb-hint">{cf ? `${fmtDate(cf.asOf)} itibarıyla` : ""}</span>
        </div>
        {!cf ? <div className="tb-loading"><span className="tb-spin" /> Yükleniyor</div> : cf.currencies.length === 0 ? <div className="tb-blank"><b>Nakit akış için veri yok</b>Kasa hesabı, açık cari kalem ya da çek girildikçe tahmin oluşur.</div> : cf.currencies.map((c) => <Flow key={c.currency} c={c} />)}
      </>)}

      <AlertModal isOpen={alert.isOpen} message={alert.message} type={alert.type} onClose={() => setAlert((a) => ({ ...a, isOpen: false }))} />
    </div>
  );
};

export default TrialBalance;
